import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { customAxiosBE } from '../../api/config';

export const fetchExpiryNotifications = createAsyncThunk(
    'expiryNotifications/fetchUnread',
    async () => {
        const response = await customAxiosBE.get('/expiry-notifications');
        return response.data;
    }
);

export const fetchAllExpiryNotifications = createAsyncThunk(
    'expiryNotifications/fetchAll',
    async () => {
        const response = await customAxiosBE.get('/expiry-notifications/all');
        return response.data;
    }
);

export const fetchExpiryNotificationCount = createAsyncThunk(
    'expiryNotifications/fetchCount',
    async () => {
        const response = await customAxiosBE.get('/expiry-notifications/count');
        return response.data.unreadCount;
    }
);

export const markExpiryNotificationAsRead = createAsyncThunk(
    'expiryNotifications/markAsRead',
    async (id) => {
        await customAxiosBE.put(`/expiry-notifications/${id}/read`);
        return id;
    }
);

export const dismissExpiryNotification = createAsyncThunk(
    'expiryNotifications/dismiss',
    async (id) => {
        await customAxiosBE.put(`/expiry-notifications/${id}/dismiss`);
        return id;
    }
);

export const markAllExpiryNotificationsAsRead = createAsyncThunk(
    'expiryNotifications/markAllAsRead',
    async () => {
        await customAxiosBE.put('/expiry-notifications/read-all');
        return true;
    }
);

export const fetchNotificationHistory = createAsyncThunk(
    'expiryNotifications/fetchHistory',
    async () => {
        const response = await customAxiosBE.get('/expiry-notifications/history');
        return response.data;
    }
);

export const fetchNotificationPreferences = createAsyncThunk(
    'expiryNotifications/fetchPreferences',
    async () => {
        const response = await customAxiosBE.get('/expiry-notifications/preferences');
        return response.data;
    }
);

export const updateNotificationPreferences = createAsyncThunk(
    'expiryNotifications/updatePreferences',
    async (preferences) => {
        const response = await customAxiosBE.put('/expiry-notifications/preferences', preferences);
        return response.data;
    }
);

const initialState = {
    notifications: [],
    history: [],
    unreadCount: 0,
    loading: false,
    historyLoading: false,
    notificationsEnabled: true,
    emailNotificationsEnabled: true,
};

const expiryNotificationsSlice = createSlice({
    name: 'expiryNotifications',
    initialState,
    reducers: {
        updateUnreadCount: (state, { payload }) => {
            state.unreadCount = payload;
        }
    },
    extraReducers: {
        [fetchExpiryNotifications.pending]: (state) => {
            state.loading = true;
        },
        [fetchExpiryNotifications.fulfilled]: (state, { payload }) => {
            state.notifications = payload;
            state.unreadCount = payload.filter(n => !n.isRead).length;
            state.loading = false;
        },
        [fetchExpiryNotifications.rejected]: (state) => {
            state.loading = false;
        },
        [fetchAllExpiryNotifications.pending]: (state) => {
            state.loading = true;
        },
        [fetchAllExpiryNotifications.fulfilled]: (state, { payload }) => {
            state.notifications = payload;
            state.unreadCount = payload.filter(n => !n.isRead).length;
            state.loading = false;
        },
        [fetchAllExpiryNotifications.rejected]: (state) => {
            state.loading = false;
        },
        [fetchExpiryNotificationCount.fulfilled]: (state, { payload }) => {
            state.unreadCount = payload;
        },
        [markExpiryNotificationAsRead.fulfilled]: (state, { payload: id }) => {
            const notification = state.notifications.find(n => n.id === id);
            if (notification) {
                notification.isRead = true;
            }
            state.unreadCount = state.notifications.filter(n => !n.isRead).length;
        },
        [dismissExpiryNotification.fulfilled]: (state, { payload: id }) => {
            state.notifications = state.notifications.filter(n => n.id !== id);
            state.unreadCount = state.notifications.filter(n => !n.isRead).length;
        },
        [markAllExpiryNotificationsAsRead.fulfilled]: (state) => {
            state.notifications = state.notifications.map(n => ({ ...n, isRead: true }));
            state.unreadCount = 0;
        },
        [fetchNotificationHistory.pending]: (state) => {
            state.historyLoading = true;
        },
        [fetchNotificationHistory.fulfilled]: (state, { payload }) => {
            state.history = payload;
            state.historyLoading = false;
        },
        [fetchNotificationHistory.rejected]: (state) => {
            state.historyLoading = false;
        },
        [fetchNotificationPreferences.fulfilled]: (state, { payload }) => {
            state.notificationsEnabled = payload.notificationsEnabled;
            state.emailNotificationsEnabled = payload.emailNotificationsEnabled;
        },
        [updateNotificationPreferences.fulfilled]: (state, { payload }) => {
            state.notificationsEnabled = payload.notificationsEnabled;
            state.emailNotificationsEnabled = payload.emailNotificationsEnabled;
        },
    }
});

export const { updateUnreadCount } = expiryNotificationsSlice.actions;
export const expiryNotificationsReducer = expiryNotificationsSlice.reducer;
